import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { type ReactNode, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import type { SharedLink } from "../../shared/links";
import { isRouteType, ROUTE_TYPES } from "../../shared/stepConfig";
import { useRecordListControlChange } from "../hooks/useRecordListControlChange";
import type { DisplayableSpace } from "../hooks/useSpaceDisplayName";
import {
  ROUTE_TYPE_DESCRIPTIONS,
  ROUTE_TYPE_ICONS,
  ROUTE_TYPE_INLINE_LABELS,
  ROUTE_TYPE_LABELS,
} from "../lib/routeTypeDisplay";
import { useMakeUrl, useCurrentUrl } from "../lib/urls";
import type { RouteNode } from "../lib/urls/types";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { EmptyState } from "./EmptyState";
import { Icon, type IconName } from "./Icon";
import { InfiniteScroll } from "./InfiniteScroll";
import { Link } from "./Link";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
} from "./ListFilter";
import { ListTable } from "./ListTable";
import { PageCard, SharedLinkCard } from "./PageCard";
import {
  type LinkListCard,
  linkCardStepName,
  visiblePageCardRoutes,
} from "./pageCardRoutes";
import { Text } from "./Text";

const Outer = styled("div")`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`;

const SecondaryHeader = styled("div")`
  position: sticky;
  top: var(--sticky-top, 0px);
  z-index: 3;
  background: var(--surface-primary);
  border-bottom: 0.5px solid var(--border-alpha);
  display: flex;
  align-items: center;
  padding: 8px 24px 8px 12px;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 8px 12px;
  }
`;

const FilterPill = styled(Link)`
  all: unset;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 24px;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;

  &[data-selected="true"] {
    background: var(--alpha4);
    color: var(--text-primary);
  }
  &:hover {
    background: var(--alpha4);
    color: var(--text-primary);
  }
`;

const SortSpacer = styled("div")`
  margin-left: auto;
`;

const FilterFooter = styled("div")`
  position: sticky;
  bottom: 0;
  margin-top: auto;
  z-index: 1;
  background: var(--surface-primary);
  box-shadow: 0 -0.5px 0 var(--border-alpha);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 24px;
  min-height: 36px;

  @media (max-width: 768px) {
    padding: 8px 12px;
  }
`;

const SORT_OPTIONS = [
  { key: "updated", label: "Recently updated", icon: "sparkles" },
  { key: "name", label: "Name", icon: "arrow-down-a-z" },
] as const;

type SortKey = (typeof SORT_OPTIONS)[number]["key"];

const Grid = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  padding: 32px;
  align-content: start;
  align-items: start;
`;

const EmptyFilterArea = styled("div")`
  flex: 1;
  min-height: 0;
`;

const PAGE_SIZE = 200;

// Only the fields the cards read, so both Zero rows and the Links endpoint's
// plain JSON satisfy them.
export type AppRoute = {
  readonly route: string;
  readonly stepDir: string;
  readonly routeType: string | null;
  readonly routeAuth: string | null;
  readonly title: string | null;
  readonly externalId: string | null;
};
export type AppBranch = {
  readonly id: string;
  readonly name: string;
  readonly headCommit?: { readonly routes: readonly AppRoute[] } | null;
};
export type AppWorkflow = {
  readonly id: string;
  readonly name: string;
  readonly pausedAt: number | null;
  readonly updatedAt: number;
  readonly branches: readonly AppBranch[];
};
export type AppSpace = DisplayableSpace & {
  readonly slug: string;
  readonly customHostname?: string | null;
  readonly ownerName?: string | null;
};

export type PageRow = {
  workflow: AppWorkflow;
  space: AppSpace;
  // When set, the card shows a generic label instead of the space name, for a
  // space the viewer isn't a member of (the Links gallery).
  hideSpaceName?: boolean;
};

export type PageItem = PageRow | { readonly shared: SharedLink };

function itemUpdatedAt(item: PageItem): number {
  return "shared" in item ? 0 : item.workflow.updatedAt;
}

export { countPageCards } from "./pageCardRoutes";

type Props = {
  rows: readonly PageItem[];
  linksRoute: RouteNode;
  showSpaceColumn: boolean;
  emptyTitle: string;
  emptyDescription: string;
  isComplete: boolean;
  // Favorite an individual link (route) rather than its workflow: the card's
  // heart reflects and toggles the route favorite, matching the Links gallery.
  routeFavorites?: boolean;
  // Only ever render route-favorited links (the Favorites page), with no
  // toggle. Pair with routeFavorites so unfavoriting drops the card.
  favoritesOnly?: boolean;
  filters?: FilterDef[];
  headerActions?: ReactNode;
  // Rows arrive already filtered, sorted, and paged by the server, so they
  // render in order and the end of the list asks for the next page.
  paging?: { hasMore: boolean; onLoadMore: () => void };
};

export function PagesList({
  rows,
  linksRoute,
  showSpaceColumn,
  emptyTitle,
  emptyDescription,
  isComplete,
  routeFavorites = false,
  favoritesOnly = false,
  filters,
  headerActions,
  paging,
}: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [favoriteIds] = useQuery(queries.userFavoriteWorkflowIds());
  const favoriteSet = new Set(favoriteIds.map((f) => f.workflowId));
  const [routeFavoriteIds] = useQuery(queries.userFavoriteRouteIds());
  const routeFavoriteSet = new Set(
    routeFavoriteIds.map((f) => `${f.workflowId}:${f.stepDir}`)
  );
  const recordControl = useRecordListControlChange();
  const { type, sort: sortParam } = useCurrentUrl(linksRoute) as {
    type?: string;
    sort?: string;
  };
  const typeFilter = isRouteType(type) ? type : "webpage";
  const favFilter = favoritesOnly;
  const defaultSort: SortKey = "updated";
  const sort: SortKey = SORT_OPTIONS.some((o) => o.key === sortParam)
    ? (sortParam as SortKey)
    : defaultSort;

  const [limit, setLimit] = useState(PAGE_SIZE);
  // Reset the window only on the in-component controls that redefine it. The
  // upstream search/scope filters arrive through `rows`, and keying on
  // `rows.length` would reset the window (jumping the scroll) mid-sync or on any
  // unrelated workflow create/delete/move.
  const limitResetKey = `${typeFilter} ${favFilter} ${sort}`;
  const [prevLimitResetKey, setPrevLimitResetKey] = useState(limitResetKey);
  if (limitResetKey !== prevLimitResetKey) {
    setPrevLimitResetKey(limitResetKey);
    setLimit(PAGE_SIZE);
  }

  const sortedRows =
    paging || sort === "name"
      ? rows
      : [...rows].sort((a, b) => itemUpdatedAt(b) - itemUpdatedAt(a));

  const filtersActive = filters != null && hasActiveFilters(filters);

  // Keep the subheader mounted when a filter empties the list, so its pills stay
  // reachable to clear; only bail out when there are genuinely no links.
  if (rows.length === 0 && !filtersActive && !headerActions) {
    return isComplete ? (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        iconName="product-cases"
      />
    ) : null;
  }

  const expandedCards = sortedRows.flatMap((item): LinkListCard[] => {
    if (!("shared" in item)) {
      return visiblePageCardRoutes([item], {
        typeFilter,
        favFilter,
        routeFavoriteSet,
      }).map((entry) => ({ entry }));
    }
    const hidden = favFilter || item.shared.routeType !== typeFilter;
    return hidden ? [] : [{ link: item.shared }];
  });
  const cards =
    paging || sort !== "name"
      ? expandedCards
      : [...expandedCards].sort((a, b) =>
          linkCardStepName(a).localeCompare(linkCardStepName(b), undefined, {
            sensitivity: "base",
          })
        );
  const hasMore = paging ? paging.hasMore : cards.length > limit;
  const linkItems = (paging ? cards : cards.slice(0, limit)).map((card) => {
    if ("link" in card)
      return {
        key: card.link.url,
        content: <SharedLinkCard key={card.link.url} link={card.link} />,
      };
    const {
      row: { workflow, space, hideSpaceName },
      branch,
      route,
      webpageRoutes,
    } = card.entry;
    return {
      key: `${workflow.id}-${branch.id}-${route.stepDir}`,
      content: (
        <PageCard
          key={`${workflow.id}-${branch.id}-${route.stepDir}`}
          workflow={workflow}
          branch={branch}
          route={route}
          webpageRoutes={webpageRoutes}
          space={space}
          showSpaceColumn={showSpaceColumn}
          hideSpaceName={hideSpaceName}
          isFavorite={
            routeFavorites
              ? routeFavoriteSet.has(`${workflow.id}:${route.stepDir}`)
              : favoriteSet.has(workflow.id)
          }
          onToggleFavorite={
            routeFavorites
              ? () =>
                  z.mutate(
                    sharedMutators.toggleRouteFavorite({
                      workflowId: workflow.id,
                      stepDir: route.stepDir,
                    })
                  )
              : undefined
          }
          showSelectStepAction={false}
          showOpenWorkflowAction
        />
      ),
    };
  });

  const emptyCopy: { title: string; description: string; iconName: IconName } =
    favFilter
      ? {
          title: "No favorite links",
          description: "Favorite a link to see it here.",
          iconName: "heart",
        }
      : {
          title: `No ${ROUTE_TYPE_INLINE_LABELS[typeFilter]} links`,
          description: ROUTE_TYPE_DESCRIPTIONS[typeFilter],
          iconName: ROUTE_TYPE_ICONS[typeFilter],
        };

  return (
    <Outer>
      <SecondaryHeader>
        {ROUTE_TYPES.map((t) => (
          <FilterPill
            key={t}
            to={makeUrl(linksRoute, { type: t })}
            data-selected={typeFilter === t ? "true" : "false"}
            onClick={() => recordControl("filter_type", t)}
          >
            <Icon name={ROUTE_TYPE_ICONS[t]} size={16} />
            <Text variant="text-small-md">{ROUTE_TYPE_LABELS[t]}</Text>
          </FilterPill>
        ))}
        <SortSpacer />
        {headerActions}
        <Dropdown
          align="bottom"
          minWidth={180}
          opener={
            <Button
              variant="secondary"
              icon="arrow-down-up"
              aria-label="Sort"
            />
          }
          sections={[
            {
              items: SORT_OPTIONS.map((o) => ({
                type: "link" as const,
                key: o.key,
                to: makeUrl(linksRoute, {
                  sort: o.key === defaultSort ? "" : o.key,
                }),
                onClick: () => {
                  if (sort !== o.key) recordControl("sort", o.key);
                },
                left: <Icon name={o.icon} size={16} />,
                label: o.label,
                right: (
                  <Icon
                    name="check"
                    size={12}
                    color={
                      sort === o.key ? "var(--text-secondary)" : "transparent"
                    }
                  />
                ),
              })),
            },
          ]}
        />
        {filters && <ListFilter filters={filters} />}
      </SecondaryHeader>
      {cards.length === 0 && isComplete ? (
        <EmptyFilterArea>
          <EmptyState
            title={emptyCopy.title}
            description={emptyCopy.description}
            iconName={emptyCopy.iconName}
          />
        </EmptyFilterArea>
      ) : (
        <>
          {typeFilter === "webpage" ? (
            <Grid>{linkItems.map((item) => item.content)}</Grid>
          ) : (
            <ListTable
              hideHeaders
              headers={{ content: "Link" }}
              items={linkItems}
            />
          )}
          {hasMore && (
            <InfiniteScroll
              hasMore
              onLoadMore={
                paging
                  ? paging.onLoadMore
                  : () => setLimit((p) => p + PAGE_SIZE)
              }
            />
          )}
        </>
      )}
      {filtersActive && (
        <FilterFooter>
          <ListFilterPills filters={filters} />
        </FilterFooter>
      )}
    </Outer>
  );
}
